import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import {
	Children,
	type ChangeEvent,
	type MouseEventHandler,
	type ReactNode,
	type SelectHTMLAttributes,
	forwardRef,
	isValidElement,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";

import { cn } from "../cn.js";
import { AnchoredPanel } from "./AnchoredPanel.js";
import { controlClassName } from "./Field.js";

interface OptionItem {
	value: string;
	label: string;
	disabled: boolean;
}

function optionLabel(children: ReactNode): string {
	if (typeof children === "string" || typeof children === "number") return String(children);
	if (Array.isArray(children)) return children.map((child) => optionLabel(child)).join("");
	return "";
}

function readOptions(children: ReactNode): OptionItem[] {
	const options: OptionItem[] = [];
	Children.forEach(children, (child) => {
		if (!isValidElement(child) || child.type !== "option") return;
		const props = child.props as { value?: string | number; disabled?: boolean; children?: ReactNode };
		const label = optionLabel(props.children).trim();
		options.push({
			value: props.value === undefined ? label : String(props.value),
			label: label || String(props.value ?? ""),
			disabled: Boolean(props.disabled),
		});
	});
	return options;
}

/**
 * Listbox styled like the other fields.
 *
 * Call sites still pass `<option>` children and read `event.target.value`.
 * The native popup is what overlapped the field and ignored the page layout.
 */
type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
	/** When true, the menu matches the trigger width. Default true for form fields. */
	matchWidth?: boolean;
	panelClassName?: string;
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
	{
		className,
		children,
		value,
		defaultValue,
		onChange,
		disabled,
		id,
		required,
		name,
		"aria-describedby": describedBy,
		"aria-invalid": invalid,
		"aria-label": ariaLabel,
		"aria-labelledby": labelledBy,
		onMouseDown,
		matchWidth = true,
		panelClassName,
	},
	ref,
) {
	const options = readOptions(children);
	const listId = useId();
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);
	const [uncontrolled, setUncontrolled] = useState(() =>
		defaultValue === undefined ? (options[0]?.value ?? "") : String(defaultValue),
	);
	const current = value === undefined ? uncontrolled : String(value);
	const selectedIndex = Math.max(
		0,
		options.findIndex((option) => option.value === current),
	);
	const [active, setActive] = useState(selectedIndex);
	const selected = options.find((option) => option.value === current);

	const close = (restoreFocus: boolean) => {
		setOpen(false);
		if (restoreFocus) buttonRef.current?.focus();
	};

	const choose = (option: OptionItem) => {
		if (option.disabled) return;
		if (option.value !== current && value === undefined) setUncontrolled(option.value);
		if (option.value !== current) {
			onChange?.({
				target: { value: option.value, name: name ?? "" },
				currentTarget: { value: option.value, name: name ?? "" },
			} as ChangeEvent<HTMLSelectElement>);
		}
		close(true);
	};

	useEffect(() => {
		if (!open) return;
		listRef.current?.focus();
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const panel = listRef.current;
		const item = document.getElementById(`${listId}-option-${active}`);
		if (!panel || !item) return;
		const panelRect = panel.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();
		if (itemRect.bottom > panelRect.bottom) panel.scrollTop += itemRect.bottom - panelRect.bottom;
		else if (itemRect.top < panelRect.top) panel.scrollTop -= panelRect.top - itemRect.top;
	}, [open, active, listId]);

	const move = (delta: number) => {
		if (options.length === 0) return;
		setActive((index) => {
			let next = index;
			for (let step = 0; step < options.length; step += 1) {
				next = (next + delta + options.length) % options.length;
				if (!options[next]?.disabled) return next;
			}
			return index;
		});
	};

	return (
		<div
			className={cn("min-w-0", className)}
			onKeyDown={(event) => {
				if (event.key === "Escape" && open) {
					event.preventDefault();
					event.stopPropagation();
					close(true);
				}
			}}
		>
			{name ? <input type="hidden" name={name} value={current} readOnly tabIndex={-1} aria-hidden="true" /> : null}
			<button
				ref={(node) => {
					buttonRef.current = node;
					if (typeof ref === "function") ref(node);
					else if (ref) ref.current = node;
				}}
				id={id}
				type="button"
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={listId}
				aria-label={ariaLabel}
				aria-labelledby={labelledBy}
				aria-describedby={describedBy}
				aria-invalid={invalid}
				aria-required={required || undefined}
				onMouseDown={onMouseDown as MouseEventHandler<HTMLButtonElement> | undefined}
				onClick={() => {
					if (disabled || options.length === 0) return;
					setActive(selectedIndex < 0 ? 0 : selectedIndex);
					setOpen((currentOpen) => !currentOpen);
				}}
				onKeyDown={(event) => {
					if (disabled) return;
					if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						setActive(selectedIndex < 0 ? 0 : selectedIndex);
						setOpen(true);
					}
				}}
				className={cn(
					controlClassName,
					"flex h-10 items-center justify-between gap-2 pr-2.5 text-left",
					open && "border-(--color-brand-500)",
				)}
			>
				<span className="truncate">{selected?.label ?? "Choose"}</span>
				<CaretDownIcon
					className={cn("size-4 shrink-0 text-(--text-muted) transition-transform", open && "rotate-180")}
					aria-hidden="true"
				/>
			</button>

			<AnchoredPanel
				ref={listRef}
				open={open}
				anchorRef={buttonRef}
				matchWidth={matchWidth}
				className={panelClassName}
				onDismiss={() => setOpen(false)}
				id={listId}
				role="listbox"
				aria-labelledby={id}
				aria-activedescendant={`${listId}-option-${active}`}
				tabIndex={-1}
				onMouseDown={(event) => event.preventDefault()}
				onKeyDown={(event) => {
					switch (event.key) {
						case "ArrowDown":
							event.preventDefault();
							move(1);
							break;
						case "ArrowUp":
							event.preventDefault();
							move(-1);
							break;
						case "Home":
							event.preventDefault();
							setActive(0);
							break;
						case "End":
							event.preventDefault();
							setActive(options.length - 1);
							break;
						case "Enter":
						case " ": {
							event.preventDefault();
							const option = options[active];
							if (option) choose(option);
							break;
						}
						case "Escape":
							event.preventDefault();
							close(true);
							break;
						default:
							break;
					}
				}}
			>
				{options.map((option, index) => {
					const isSelected = option.value === current;
					const isActive = index === active;
					return (
						<button
							key={`${option.value}-${index}`}
							type="button"
							role="option"
							id={`${listId}-option-${index}`}
							aria-selected={isSelected}
							disabled={option.disabled}
							title={option.label}
							onMouseEnter={() => setActive(index)}
							onClick={() => choose(option)}
							className={cn(
								"flex w-full items-center gap-2 rounded-(--radius-sm) px-2.5 py-2 text-left text-sm",
								"text-(--text-primary) outline-none",
								isActive && "bg-(--surface-sunken)",
								isSelected && "font-medium",
								option.disabled && "cursor-not-allowed opacity-50",
							)}
						>
							<span className="min-w-0 flex-1 truncate">{option.label}</span>
							{isSelected && (
								<CheckIcon className="size-4 shrink-0 text-(--color-brand-600)" weight="bold" aria-hidden="true" />
							)}
						</button>
					);
				})}
			</AnchoredPanel>
		</div>
	);
});
